import React from 'react';
import { styled } from '@linaria/react';
import {
  useActiveCode,
  SandpackPreview,
} from '@codesandbox/sandpack-react';

import { clamp } from '@/utils';

import Toolbar from './Toolbar';
import CodeEditor from './CodeEditor';
import ResultHeader from './ResultHeader';
import Console from './Console';
import { Tab } from './Sandbox.types';

interface BaseUnitProps {
  title?: string;
  outputTabs: Array<Tab>;
  activeResultTab: Tab;
  handleSelectResultTab: (tab: Tab) => void;
  splitConsole: boolean;
  minPreviewHeight: number | string;
  minEditorHeight?: number;
  maxEditorHeight?: number;
  transparentBackground: boolean;
  isRenderingPreview: boolean;
}

function UnitStacked({
  title,
  outputTabs,
  activeResultTab,
  handleSelectResultTab,
  splitConsole,
  minPreviewHeight,
  minEditorHeight = 90,
  maxEditorHeight = 625,
  transparentBackground,
  isRenderingPreview,
}: BaseUnitProps) {
  const editorWrapperHeight = useEditorWrapperHeight(
    minEditorHeight,
    maxEditorHeight
  );

  let secondColumn;

  if (splitConsole) {
    secondColumn = (
      <ResultOuterWrapper>
        <PreviewRow>
          <ResultHeader tabs={['result']} />
          <ResultWrapper
            style={{
              '--height':
                typeof minPreviewHeight === 'number'
                  ? minPreviewHeight + 'px'
                  : minPreviewHeight,
            }}
          >
            <RoundedCorners>
              {isRenderingPreview && (
                <SandpackPreview
                  showNavigator={false}
                  showOpenInCodeSandbox={false}
                  showRefreshButton={false}
                />
              )}
            </RoundedCorners>
          </ResultWrapper>
        </PreviewRow>
        <ConsoleRow>
          <ResultHeader tabs={['console']} />
          <ResultWrapper
            style={{ paddingTop: 16, paddingBottom: 16 }}
          >
            <RoundedCorners>
              <ConsoleWrapper>
                <Console />
              </ConsoleWrapper>
            </RoundedCorners>
          </ResultWrapper>
        </ConsoleRow>
      </ResultOuterWrapper>
    );
  } else {
    secondColumn = (
      <ResultOuterWrapper>
        <ResultHeader
          tabs={outputTabs}
          activeTab={activeResultTab}
          handleSelectTab={handleSelectResultTab}
        />
        <ResultWrapper
          style={{
            '--height':
              typeof minPreviewHeight === 'number'
                ? minPreviewHeight + 'px'
                : minPreviewHeight,
          }}
        >
          <RoundedCorners
            style={{
              '--iframe-background-color': transparentBackground
                ? 'hsl(210deg 15% 6%)'
                : 'transparent',
            }}
          >
            {isRenderingPreview && (
              <SandpackPreview
                showNavigator={false}
                showOpenInCodeSandbox={false}
                showRefreshButton={false}
              />
            )}

            <ConsoleWrapper
              style={{
                '--pointer-events':
                  activeResultTab === 'console' ? 'auto' : 'none',
                '--opacity': activeResultTab === 'console' ? 1 : 0,
                '--transition-delay':
                  activeResultTab === 'console' ? '0ms' : '100ms',
                '--child-delay':
                  activeResultTab === 'console' ? '200ms' : '0ms',
              }}
            >
              <Console />
            </ConsoleWrapper>
          </RoundedCorners>
        </ResultWrapper>
      </ResultOuterWrapper>
    );
  }

  return (
    <InnerWrapper>
      <Toolbar title={title} />
      <CodeEditorWrapper style={{ height: editorWrapperHeight }}>
        <CodeEditor />
      </CodeEditorWrapper>
      {secondColumn}
    </InnerWrapper>
  );
}

/**
 * So, one of the tricky things here is that Sandpack expects to
 * have a defined height. I want the sandbox to grow and shrink,
 * within reason, and so the only approach I've found is to
 * count the number of lines in the code, and set an explicit height
 * based on that.
 */
function useEditorWrapperHeight(
  minHeight: number,
  maxHeight: number
) {
  const { code } = useActiveCode();
  const numOfLines = code.match(/\n/g)?.length || 1;

  const LINE_HEIGHT = 21;
  const TAB_BAR_HEIGHT = 40;
  const MAGIC_NUMBER = 60; // Not sure why it needs this, but it does

  const height = clamp(
    numOfLines * LINE_HEIGHT + TAB_BAR_HEIGHT + MAGIC_NUMBER,
    minHeight,
    maxHeight
  );

  // Turn pixels into rems:
  return height / 16 + 'rem';
}

const InnerWrapper = styled.div`
  --color-primary: hsl(50deg 100% 50%);

  background: transparent;
  border: none;

  /*
    The following styles are only really needed in fullscreen,
    but they don't hurt here.
  */
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CodeEditorWrapper = styled.div`
  position: relative;
  scrollbar-width: thin;
`;

const ResultOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0px;
  border-top: 1px solid var(--color-gray-100);
  font-size: 0.875rem;
`;

const ResultWrapper = styled.div`
  position: relative;
  padding: 16px;
  height: var(--height);
  /* Sandpack uses z-index, so we'll flatten it all here */
  isolation: isolate;
`;

const RoundedCorners = styled.div`
  border-radius: 4px;
  overflow: hidden;
  overflow: clip;
  height: 100%;

  iframe {
    background: var(--iframe-background-color);
  }
`;

const ConsoleWrapper = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  bottom: 15px;
  padding: 1px;
  /*
    This silly z-index number is required because sandpack uses
    a z-index of 3 for the overlay, instead of relying on the
    stacking order!!
  */
  z-index: 4;
  background: var(--syntax-bg);
  transition: opacity 200ms var(--transition-delay);
  opacity: var(--opacity);
  pointer-events: var(--pointer-events);

  & > * {
    opacity: var(--opacity);
    transition: opacity 200ms var(--child-delay);
  }
`;

const PreviewRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ConsoleRow = styled.div`
  height: 250px;
  display: flex;
  flex-direction: column;
`;

export default UnitStacked;
